import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { formatPriceMwk } from "../config/subscriptionPlans";
import { fetchSubscriptionPaymentStatus } from "../utils/subscriptionPaymentApi";

const POLL_MS = 4000;

export default function SubscriptionPaymentReturnPage() {
    const [searchParams] = useSearchParams();
    const statusParam = String(searchParams.get("status") || "").toLowerCase();
    const referenceNumber = String(
        searchParams.get("ref") || searchParams.get("tx_ref") || "",
    ).trim();

    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState(null);
    const [error, setError] = useState("");
    const pollRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const pollStatus = useCallback(async () => {
        if (!referenceNumber) return;

        try {
            const { data } = await fetchSubscriptionPaymentStatus(referenceNumber);
            setPayment(data);

            if (data?.status === "Completed") {
                stopPolling();
                setLoading(false);
            } else if (data?.status === "Expired") {
                stopPolling();
                setLoading(false);
                setError("Payment window expired. Start a new payment from registration or your dashboard.");
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || "Could not verify payment";
            setError(msg);
            stopPolling();
            setLoading(false);
        }
    }, [referenceNumber, stopPolling]);

    useEffect(() => {
        if (!referenceNumber) {
            setError("Missing payment reference.");
            setLoading(false);
            return undefined;
        }

        if (statusParam === "failed") {
            setError("Payment was not completed on PayChangu. You can try again from registration or your dashboard.");
            setLoading(false);
            return undefined;
        }

        pollStatus();
        pollRef.current = setInterval(pollStatus, POLL_MS);

        return () => stopPolling();
    }, [referenceNumber, statusParam, pollStatus, stopPolling]);

    const isSuccess = payment?.status === "Completed";

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.default",
                px: 2,
                py: 4,
            }}
        >
            <Container maxWidth="sm">
                <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: "center" }}>
                    {loading && !isSuccess ? (
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress />
                            <Typography variant="h6" fontWeight={700}>
                                Confirming your payment…
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This usually takes a few seconds after you pay on PayChangu.
                            </Typography>
                        </Stack>
                    ) : null}

                    {isSuccess ? (
                        <Stack spacing={2} alignItems="center">
                            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: "success.main" }} />
                            <Typography variant="h5" fontWeight={800}>
                                Subscription activated!
                            </Typography>
                            <Typography color="text.secondary">
                                Amount received:{" "}
                                {formatPriceMwk(payment.amountReceived ?? payment.amount ?? 0)}
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/login"
                                variant="contained"
                                size="large"
                                replace
                                sx={{ mt: 1 }}
                            >
                                Sign in to your account
                            </Button>
                        </Stack>
                    ) : null}

                    {!loading && !isSuccess && error ? (
                        <Stack spacing={2} alignItems="center">
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: "warning.main" }} />
                            <Typography variant="h6" fontWeight={700}>
                                Payment not confirmed
                            </Typography>
                            <Alert severity="warning" sx={{ width: "100%", textAlign: "left" }}>
                                {error}
                            </Alert>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
                                <Button component={RouterLink} to="/Adminregister" variant="outlined">
                                    Back to registration
                                </Button>
                                <Button component={RouterLink} to="/login" variant="contained">
                                    Sign in
                                </Button>
                            </Stack>
                        </Stack>
                    ) : null}
                </Paper>
            </Container>
        </Box>
    );
}
